/** Read a picked photo as base64 (no data: prefix), as the API's *Base64 fields expect. */
export function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error ?? new Error("Could not read the photo."));
    r.onload = () => {
      const s = String(r.result ?? "");
      resolve(s.includes(",") ? s.slice(s.indexOf(",") + 1) : s);
    };
    r.readAsDataURL(file);
  });
}

export const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

/**
 * Open a private file (a card photo or PDF) in a new tab: the tab opens on the tap (so no pop-up blocker stops it), the file is
 * fetched with the signed-in session (the files route checks who may see it), then shown from a blob: URL that exists only in
 * this browser. Pop-ups blocked: it opens through a link instead.
 */
export async function openPrivateFile(load: () => Promise<Blob>): Promise<void> {
  const w = typeof window !== "undefined" ? window.open("", "_blank") : null;
  if (w) w.opener = null;
  try {
    const url = URL.createObjectURL(await load());
    if (w) w.location.href = url;
    else { const a = document.createElement("a"); a.href = url; a.target = "_blank"; a.rel = "noopener"; document.body.appendChild(a); a.click(); a.remove(); }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) { w?.close(); throw e; }
}

/** Save a blob (PDF) to the user's device. */
export function downloadBlob(blob: Blob, filename: string): void {
  if (!(blob instanceof Blob)) throw new Error("The file didn't come through. Try the download again.");
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/**
 * A phone photo can be bigger than the server takes (8 MB). Shrink it to fit instead of turning the person away:
 * longest side 2400 px, JPEG. Returns the original when it already fits, or when the browser can't do it.
 */
export async function fitPhoto(file: Blob, maxBytes: number): Promise<Blob> {
  if (file.size <= maxBytes || typeof createImageBitmap !== "function" || typeof document === "undefined") return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 2400 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale); canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d")!.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const out = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    return out && out.size < file.size ? out : file;
  } catch { return file; }
}
